/*
  The sort control stacked mode needs (§6.3).

  Below md there is no <th>, so there is nowhere to hang aria-sort. This pair of selects
  replaces it and carries the sort state itself: the two labels say what is being sorted
  and how, and the live region alongside them announces the result of a change, which is
  what aria-sort would otherwise have done.

  Every decision it makes is in ./stackedSort, tested there.
*/

import Select from "components/Select";

import {
  buildStackedSortColumnOptions,
  buildStackedSortDirectionOptions,
  describeSortState,
  resolveStackedSortSelection,
  stackedSortColumnChange,
  stackedSortDirectionChange,
} from "./stackedSort";
import { DataTableColumn, TableSortState } from "./types";

export interface StackedSortControlProps<TRow> {
  columns: readonly DataTableColumn<TRow>[];
  sort: TableSortState;
  onSortChange: (sort: TableSortState) => void;
  className?: string;
}

function StackedSortControl<TRow>(props: StackedSortControlProps<TRow>) {
  const { columns, sort, onSortChange, className = "" } = props;

  const columnOptions = buildStackedSortColumnOptions(columns);
  /* Nothing in this table sorts (D19's search results): render no control at all. */
  if (columnOptions.length === 0) {
    return null;
  }

  const selection = resolveStackedSortSelection(columns, sort);
  const directionOptions = buildStackedSortDirectionOptions(selection.column);

  const handleColumnChange = (value: string) => {
    const next = stackedSortColumnChange(columns, sort, value);
    if (next) {
      onSortChange(next);
    }
  };

  const handleDirectionChange = (value: string) => {
    const next = stackedSortDirectionChange(columns, sort, value);
    if (next) {
      onSortChange(next);
    }
  };

  return (
    <div className={`flex flex-wrap items-end gap-3 ${className}`}>
      <Select
        label="Sort by"
        value={selection.columnValue}
        options={columnOptions}
        onChange={handleColumnChange}
        className="min-w-[10rem] flex-1"
      />
      <Select
        label="Direction"
        value={selection.directionValue}
        options={directionOptions}
        onChange={handleDirectionChange}
        className="min-w-[9rem] flex-1"
      />
      <p role="status" className="sr-only">
        {describeSortState(columns, sort)}
      </p>
    </div>
  );
}

export default StackedSortControl;
